import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Button, Card, ErrorText, Spinner, Table, EmptyState } from '../../components/ui'
import {
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_LABELS,
  clp,
  formatDate,
  formatDateTime,
  formatRut,
  HEALTH_ROLE_LABELS,
  PAYMENT_METHOD_LABELS,
  insuranceLabel,
} from '../../utils/format'
import type { Appointment, Charge, Encounter, Patient, Payment } from '../../types/api'
import { Icon } from '../../components/ui/icons'
import { PatientDocumentsSection } from '../../components/documents/PatientDocumentsSection'
import { FollowUpsSection } from '../../components/clinical/FollowUpsSection'

export function PatientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const user = useAuthStore((s) => s.user)
  const isDoctor = user?.role === 'DOCTOR'
  const canSeeMoney = user && ['ADMIN', 'RECEPTION', 'ACCOUNTANT'].includes(user.role)
  const canInvite = user && ['ADMIN', 'RECEPTION'].includes(user.role)
  const [inviteInfo, setInviteInfo] = useState('')
  const [inviteError, setInviteError] = useState('')

  const patient = useQuery({
    queryKey: ['patient', id],
    queryFn: async () => (await api.get<Patient>(`/patients/${id}`)).data,
  })
  const appointments = useQuery({
    queryKey: ['appointments', 'patient', id],
    queryFn: async () => (await api.get<Appointment[]>('/appointments', { params: { patientId: id } })).data,
    enabled: !isDoctor, // el médico ve su agenda; el historial clínico va aparte
  })
  const charges = useQuery({
    queryKey: ['charges', 'patient', id],
    queryFn: async () => (await api.get<Charge[]>('/charges', { params: { patientId: id } })).data,
    enabled: Boolean(canSeeMoney),
  })
  const encounters = useQuery({
    queryKey: ['encounters', id],
    queryFn: async () => (await api.get<Encounter[]>(`/encounters/patient/${id}`)).data,
    enabled: Boolean(user && ['DOCTOR', 'ADMIN'].includes(user.role)),
  })
  const payments = useQuery({
    queryKey: ['payments', 'patient', id],
    queryFn: async () => (await api.get<Payment[]>('/payments')).data,
    enabled: Boolean(canSeeMoney),
  })

  const invite = useMutation({
    mutationFn: async () => (await api.post(`/patients/${id}/portal-invite`)).data,
    onSuccess: (data: { inviteUrl: string }) => {
      setInviteError('')
      setInviteInfo(data.inviteUrl)
    },
    onError: (err) => setInviteError(apiErrorMessage(err)),
  })

  if (patient.isLoading) return <Spinner />
  if (!patient.data) return <EmptyState message="Paciente no encontrado" />
  const p = patient.data
  const patientPayments = (payments.data ?? []).filter((pay) => pay.patient?.id === id)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-2xl font-bold text-ink">
          {p.firstName} {p.lastName}
        </h1>
        <Badge>{formatRut(p.rut)}</Badge>
        <Badge>{insuranceLabel(p)}</Badge>
        {p.company && <Badge className="inline-flex items-center gap-1 bg-amber-100 text-amber-800"><Icon name="company" className="h-3.5 w-3.5" />{p.company.businessName}</Badge>}
        {canInvite && (
          <Button variant="secondary" disabled={invite.isPending} onClick={() => invite.mutate()}>
            Invitar al portal de paciente
          </Button>
        )}
      </div>
      {inviteInfo && (
        <p className="flex flex-wrap items-center gap-1.5 rounded-lg bg-soft px-3 py-2 text-xs">
          <Icon name="check" className="h-4 w-4 shrink-0 text-emerald-600" />
          Invitación enviada al correo del paciente. Enlace de activación:{' '}
          <span className="break-all font-mono">{inviteInfo}</span>
        </p>
      )}
      <ErrorText>{inviteError}</ErrorText>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Datos de contacto">
          <dl className="space-y-1.5 text-sm">
            <div><dt className="inline font-medium">Teléfono: </dt><dd className="inline text-gray-600">{p.phone ?? '—'}</dd></div>
            <div><dt className="inline font-medium">Email: </dt><dd className="inline text-gray-600">{p.email ?? '—'}</dd></div>
            <div><dt className="inline font-medium">Nacimiento: </dt><dd className="inline text-gray-600">{p.birthDate ? formatDate(p.birthDate) : '—'}</dd></div>
            <div><dt className="inline font-medium">Deporte: </dt><dd className="inline text-gray-600">{p.sport ?? '—'}</dd></div>
            <div><dt className="inline font-medium">Alergias: </dt><dd className="inline text-gray-600">{p.allergies ?? 'Sin registro'}</dd></div>
            <div>
              <dt className="inline font-medium">Consentimiento datos: </dt>
              <dd className="inline text-gray-600">{p.consentAcceptedAt ? formatDate(p.consentAcceptedAt) : 'Pendiente'}</dd>
            </div>
          </dl>
        </Card>

        {user && ['DOCTOR', 'ADMIN'].includes(user.role) && (
          <Card title="Historial clínico" className="lg:col-span-2">
            {encounters.isLoading ? (
              <Spinner />
            ) : (encounters.data?.length ?? 0) === 0 ? (
              <EmptyState message="Sin atenciones registradas (o sin atenciones tuyas con este paciente)." />
            ) : (
              <ul className="space-y-2">
                {(encounters.data ?? []).map((e) => (
                  <li key={e.id} className="rounded-lg border border-gray-200 p-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-ink">
                        {e.appointment?.service.name ?? 'Atención'} · {e.appointment ? formatDateTime(e.appointment.startsAt) : formatDateTime(e.startedAt)}
                      </p>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge className={e.status === 'CLOSED' ? 'bg-gray-200 text-gray-700' : 'bg-brand-200 text-brand-800'}>
                          {e.status === 'CLOSED' ? 'Cerrada' : 'Abierta'}
                        </Badge>
                        <Link to={`/app/atencion/${e.id}`} className="text-xs font-medium text-brand-700 hover:underline">
                          Abrir
                        </Link>
                      </div>
                    </div>
                    {e.professional && (
                      <p className="text-xs text-gray-500">
                        {e.professional.firstName} {e.professional.lastName} · {HEALTH_ROLE_LABELS[e.professional.healthRole]}
                      </p>
                    )}
                    {e.diagnosis && <p className="mt-1"><strong>Dg:</strong> {e.diagnosis}</p>}
                    {e.indications && <p><strong>Indicaciones:</strong> {e.indications}</p>}
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-xs text-gray-400">Acceso a ficha clínica auditado (Ley 20.584).</p>
          </Card>
        )}
      </div>

      {/* Exámenes y controles: los ve y gestiona todo el equipo clínico y de
          recepción — son la materia prima del portal del paciente. */}
      {user && ['ADMIN', 'DOCTOR', 'RECEPTION'].includes(user.role) && (
        <>
          <PatientDocumentsSection patientId={id!} />
          <FollowUpsSection patientId={id!} />
        </>
      )}

      {!isDoctor && (
        <Card title="Historial de citas">
          {appointments.isLoading ? (
            <Spinner />
          ) : (appointments.data?.length ?? 0) === 0 ? (
            <EmptyState message="Sin citas registradas." />
          ) : (
            <Table headers={['Fecha', 'Prestación', 'Profesional', 'Estado']}>
              {(appointments.data ?? []).map((a) => (
                <tr key={a.id}>
                  <td className="px-2 py-2">{formatDateTime(a.startsAt)}</td>
                  <td className="px-2 py-2">{a.service.name}</td>
                  <td className="px-2 py-2">{a.professional.firstName} {a.professional.lastName}</td>
                  <td className="px-2 py-2">
                    <Badge className={APPOINTMENT_STATUS_COLORS[a.status]}>{APPOINTMENT_STATUS_LABELS[a.status]}</Badge>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {canSeeMoney && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Cargos">
            {(charges.data?.length ?? 0) === 0 ? (
              <EmptyState message="Sin cargos." />
            ) : (
              <Table headers={['Detalle', 'Monto', 'Estado']}>
                {(charges.data ?? []).map((c) => (
                  <tr key={c.id}>
                    <td className="px-2 py-2">{c.description}</td>
                    <td className="px-2 py-2">{clp(c.amount)}</td>
                    <td className="px-2 py-2">
                      <Badge className={c.status === 'PAID' ? 'bg-emerald-100 text-emerald-800' : c.status === 'PENDING' ? 'bg-amber-100 text-amber-800' : undefined}>
                        {c.status === 'PAID' ? 'Pagado' : c.status === 'PENDING' ? 'Pendiente' : c.status === 'PARTIALLY_PAID' ? 'Abonado' : 'Anulado'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
          <Card title="Pagos">
            {patientPayments.length === 0 ? (
              <EmptyState message="Sin pagos registrados." />
            ) : (
              <Table headers={['Folio', 'Fecha', 'Medio', 'Monto']}>
                {patientPayments.map((pay) => (
                  <tr key={pay.id}>
                    <td className="px-2 py-2 font-mono text-xs">#{pay.receiptNumber}</td>
                    <td className="px-2 py-2">{formatDateTime(pay.paidAt)}</td>
                    <td className="px-2 py-2">{PAYMENT_METHOD_LABELS[pay.method]}</td>
                    <td className="px-2 py-2 font-medium">{clp(pay.amount)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}
